import type { HibouEffect } from '../../../data/full/enracine';
import { isBeatTick } from '../../../shared/tempo';
import { distanceSquared } from '../../../shared/vec';
import { hurtEnemy, keepWhere } from '../../effects';
import { applyEnemyStatus } from '../../enemy-status';
import type { SkillModule } from '../../skills/types';
import type { EnemyState, PlayerState, PropState } from '../../state';
import type { StepContext } from '../../systems/types';
import { closeIn, flyBack, ownerOf } from '../../props';

export const OWL = 'owl';

export const hibou: SkillModule<HibouEffect> = {
  cast(ctx, player, effect, _input, power) {
    const { state, tempo } = ctx;
    const ticks = effect.bars * tempo.ticksPerBar;
    const hovering = state.props?.find((prop) => prop.kind === OWL && prop.playerId === player.id);
    if (hovering !== undefined) {
      hovering.ticksLeft = ticks;
      hovering.damage = effect.damage * power;
      return;
    }
    const owl: PropState = {
      id: state.nextEntityId,
      kind: OWL,
      playerId: player.id,
      phase: 'held',
      x: player.x,
      y: player.y,
      prevX: player.x,
      prevY: player.y,
      vx: 0,
      vy: 0,
      damage: effect.damage * power,
      ticksLeft: ticks,
      hitsLeft: 0,
    };
    state.nextEntityId += 1;
    (state.props ??= []).push(owl);
  },

  update(ctx) {
    const props = ctx.state.props;
    if (props === undefined) {
      return;
    }
    let gone = false;
    for (const prop of props) {
      if (prop.kind !== OWL) {
        continue;
      }
      const owner = ownerOf(ctx.state, prop);
      const effect = effectOf(ctx, owner);
      hunt(ctx, prop, owner, effect);
      prop.ticksLeft -= 1;
      gone ||= prop.ticksLeft <= 0 && prop.phase === 'held';
    }
    if (gone) {
      keepWhere(props, (prop) => prop.kind !== OWL || prop.ticksLeft > 0 || prop.phase !== 'held');
      if (props.length === 0) {
        delete ctx.state.props;
      }
    }
  },
};

function effectOf(ctx: StepContext, owner: PlayerState): HibouEffect {
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'hibou') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no owl`);
  }
  return effect;
}

function hunt(ctx: StepContext, owl: PropState, owner: PlayerState, effect: HibouEffect): void {
  const { state, tempo } = ctx;
  if (owl.phase !== 'flying' && owl.ticksLeft > 0 && isBeatTick(state.tick, tempo)) {
    const prey = nearestTo(state.enemies, owner, effect.reach);
    if (prey !== undefined) {
      owl.phase = 'flying';
      owl.targetId = prey.id;
    }
  }
  if (owl.phase === 'held') {
    owl.x = owner.x;
    owl.y = owner.y;
    return;
  }
  if (owl.phase === 'returning') {
    flyBack(owl, owner, effect.speed);
    return;
  }
  let prey = state.enemies.find((enemy) => enemy.id === owl.targetId && enemy.hp > 0);
  prey ??= nearestTo(state.enemies, owner, effect.reach);
  if (prey === undefined) {
    flyHome(owl);
    return;
  }
  owl.targetId = prey.id;
  const from = { x: owl.x, y: owl.y };
  if (!closeIn(owl, prey, prey.radius, effect.speed)) {
    return;
  }
  hurtEnemy(state, prey, owl.damage, owner.id, from);
  applyEnemyStatus(state, prey, 'frozen', effect.frozenBeats * tempo.ticksPerBeat);
  flyHome(owl);
}

function flyHome(owl: PropState): void {
  owl.phase = 'returning';
  delete owl.targetId;
}

function nearestTo(
  enemies: readonly EnemyState[],
  from: PlayerState,
  reach: number,
): EnemyState | undefined {
  let nearest: EnemyState | undefined;
  let best = Infinity;
  for (const enemy of enemies) {
    const away = reach + enemy.radius;
    const distance = distanceSquared(enemy, from);
    if (enemy.hp > 0 && distance <= away * away && distance < best) {
      nearest = enemy;
      best = distance;
    }
  }
  return nearest;
}
